import type {
  DeviceType,
  PlatformApi,
  SetNavigationBarParams,
  Tokens,
} from "@/app/platform/types";
import { getDeviceTypeFromUserAgent } from "@/app/platform/utils";
import { SignInError } from "@/errors/SignInError";
import { devLocaleStorage } from "@/utils/localStorage";

import { bnplPaymentProvider } from "../payments/bnplPaymentProvider";
import { sandboxPaymentProvider } from "../payments/sandboxPaymentProvider";
import {
  type PaymentProviderType,
  type PaymentProvider,
  type BnplPaymentProvider,
  type StandardPaymentProvider,
} from "../payments/types";

class WebApi implements PlatformApi {
  deviceType: DeviceType = getDeviceTypeFromUserAgent();

  getLocale() {
    return new Promise<string>((resolve) => {
      setTimeout(
        () => {
          resolve(
            devLocaleStorage.get() ?? import.meta.env.VITE_PUBLIC_DEV_LOCALE!,
          );
        },
        Math.random() * 100 + 50, // random delay between 50ms and 150ms to simulate network request
      );
    });
  }

  setNavigationBar({ backgroundColor, image }: SetNavigationBarParams) {
    // if dev wrapper is used
    window.parent.postMessage(
      {
        type: "SET_NAVIGATION_BAR",
        payload: {
          backgroundColor,
          image,
        },
      },
      window.location.origin,
    );
  }

  navigateToMiniApp() {
    alert(`Mini app navigation is not supported in the web environment.`);
  }

  async signIn() {
    return new Promise<Tokens>((resolve, reject) => {
      setTimeout(
        () => {
          const accessToken = import.meta.env.VITE_PUBLIC_DEV_ACCESS_TOKEN;
          const refreshToken = import.meta.env.VITE_PUBLIC_DEV_REFRESH_TOKEN;

          if (!accessToken || !refreshToken) {
            reject(
              new SignInError(
                "Access and refresh tokens not configured in environment variables.",
                "no_tokens_available",
              ),
            );
            return;
          }

          resolve({
            accessToken,
            refreshToken,
          });
        },
        Math.random() * 1000 + 500, // random delay between 500ms and 1500ms to simulate network request
      );
    });
  }

  async updatePersonalData() {
    return new Promise<void>((resolve) => {
      setTimeout(
        () => {
          // no actual update on web
          resolve();
        },
        Math.random() * 1000 + 500, // random delay between 500ms and 1500ms to simulate network request
      );
    });
  }

  getPaymentProvider(providerType: "bnpl"): BnplPaymentProvider;
  getPaymentProvider(providerType: "standard"): StandardPaymentProvider;
  getPaymentProvider(providerType: PaymentProviderType): PaymentProvider {
    switch (providerType) {
      case "bnpl":
        return bnplPaymentProvider;
      case "standard":
        return sandboxPaymentProvider;
      default:
        throw new Error("Unsupported payment provider type");
    }
  }

  async prepareEligibilityCheck() {
    // Web platform always uses card numbers
    return Promise.resolve({ cardNumber: "6330153657143355" });
  }

  async chooseImage() {
    return new Promise<File | null>((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";
      input.onchange = () => {
        const file = input.files?.[0] || null;
        document.body.removeChild(input);
        resolve(file);
      };

      document.body.appendChild(input);
      input.click();
    });
  }
}

export const webApi: PlatformApi = new WebApi();
